import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import Container from '@/components/landing/container'
import Logo from '@/components/landing/logo'

import { LINKS, NAV_ITEMS } from '@/constants/landing'

const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!menuOpen) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        const onResize = (): void => {
            if (window.innerWidth >= 1024) setMenuOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [menuOpen])

    const closeMenu = (): void => setMenuOpen(false)

    const elevated = scrolled || menuOpen

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300',
                elevated
                    ? 'border-border/80 bg-cream/90 shadow-[0_10px_30px_-22px_rgba(16,22,90,0.45)] backdrop-blur-md'
                    : 'border-transparent bg-transparent'
            )}
        >
            <Container className='flex h-18 items-center justify-between gap-6'>
                <a
                    href='#hero'
                    onClick={closeMenu}
                    className='shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary'
                >
                    <Logo />
                </a>

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_ITEMS.map(item => (
                            <li key={item.href}>
                                <a
                                    href={item.href}
                                    className='rounded-full px-3 py-2 text-[15px] font-medium text-foreground/80 transition-colors hover:bg-secondary hover:text-primary xl:px-4'
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='hidden items-center gap-2 lg:flex'>
                    <a
                        href={LINKS.learningPortal}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='hidden items-center gap-1 rounded-full px-4 py-2 text-[15px] font-semibold text-primary transition-colors hover:bg-secondary xl:inline-flex'
                    >
                        Learning portal
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                        <span className='sr-only'>(opens in a new tab)</span>
                    </a>

                    <a
                        href='#contact'
                        className='inline-flex h-11 items-center rounded-full bg-primary px-5 text-[15px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-brand-navy'
                    >
                        Request a callback
                    </a>
                </div>

                <button
                    type='button'
                    onClick={() => setMenuOpen(open => !open)}
                    aria-expanded={menuOpen}
                    aria-controls='mobile-menu'
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    className='inline-flex size-11 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-primary/40 lg:hidden'
                >
                    {menuOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                </button>
            </Container>

            <div id='mobile-menu' hidden={!menuOpen} className='border-t border-border bg-cream lg:hidden'>
                <Container className='pb-6 pt-3'>
                    <nav aria-label='Mobile'>
                        <ul className='flex flex-col'>
                            {NAV_ITEMS.map(item => (
                                <li key={item.href}>
                                    <a
                                        href={item.href}
                                        onClick={closeMenu}
                                        className='block rounded-xl px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-secondary hover:text-primary'
                                    >
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='mt-4 grid gap-3 sm:grid-cols-2'>
                        <a
                            href='#contact'
                            onClick={closeMenu}
                            className='inline-flex h-12 items-center justify-center rounded-full bg-primary px-5 font-semibold text-primary-foreground'
                        >
                            Request a callback
                        </a>

                        <a
                            href={LINKS.learningPortal}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={closeMenu}
                            className='inline-flex h-12 items-center justify-center gap-1.5 rounded-full border border-border bg-card px-5 font-semibold text-primary'
                        >
                            Learning portal
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                            <span className='sr-only'>(opens in a new tab)</span>
                        </a>
                    </div>
                </Container>
            </div>
        </header>
    )
}

export default SiteHeader
